/**
 * Connected-devices registry for Nanode multi-device sync.
 *
 * Each device records itself when it pushes or pulls from Nanode.
 * The registry is stored in localStorage and also carried in the sync
 * snapshot so all devices can show the full list in Settings.
 */

import type { DeviceRole } from "./deviceRole";

export type ConnectedDevice = {
  /** Stable UUID generated on first install. */
  deviceId: string;
  /** User-visible device name (platform / hostname, if available). */
  name: string;
  /** "main" | "secondary" at the time of last sync. */
  role: DeviceRole;
  /** ISO-8601 UTC timestamp of last Nanode push or pull from this device. */
  lastSeenUtc: string;
};

export const CONNECTED_DEVICES_KEY = "ns.dispatch.connectedDevices";

export function hydrateConnectedDevice(raw: unknown): ConnectedDevice | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const deviceId = typeof r["deviceId"] === "string" ? r["deviceId"].trim() : "";
  const name = typeof r["name"] === "string" ? r["name"].trim() : "";
  const role: DeviceRole = r["role"] === "secondary" ? "secondary" : "main";
  const lastSeenUtc = typeof r["lastSeenUtc"] === "string" ? r["lastSeenUtc"] : new Date().toISOString();
  if (!deviceId) return null;
  return { deviceId, name: name || deviceId, role, lastSeenUtc };
}

export function hydrateConnectedDevices(raw: unknown): ConnectedDevice[] {
  if (!Array.isArray(raw)) return [];
  const out: ConnectedDevice[] = [];
  for (const item of raw) {
    const d = hydrateConnectedDevice(item);
    if (d) out.push(d);
  }
  return out;
}

export function loadConnectedDevices(
  storage?: Pick<Storage, "getItem"> | null,
): ConnectedDevice[] {
  try {
    const raw = storage?.getItem(CONNECTED_DEVICES_KEY);
    if (!raw) return [];
    return hydrateConnectedDevices(JSON.parse(raw) as unknown);
  } catch {
    return [];
  }
}

export function saveConnectedDevices(
  devices: ConnectedDevice[],
  storage?: Pick<Storage, "setItem"> | null,
): void {
  try {
    storage?.setItem(CONNECTED_DEVICES_KEY, JSON.stringify(devices));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Merge a device record into the list.
 * If a device with the same deviceId already exists it is updated in-place;
 * otherwise it is appended. Returns the new list (immutable).
 */
export function upsertConnectedDevice(
  devices: ConnectedDevice[],
  device: ConnectedDevice,
): ConnectedDevice[] {
  const idx = devices.findIndex((d) => d.deviceId === device.deviceId);
  if (idx === -1) return [...devices, device];
  const updated = [...devices];
  updated[idx] = device;
  return updated;
}

/**
 * Update last-seen and role for a device in the list.
 * Appends if not found.
 */
export function markDeviceAsSeen(
  devices: ConnectedDevice[],
  deviceId: string,
  name: string,
  role: DeviceRole,
  nowMs = Date.now(),
): ConnectedDevice[] {
  const device: ConnectedDevice = {
    deviceId,
    name,
    role,
    lastSeenUtc: new Date(nowMs).toISOString(),
  };
  return upsertConnectedDevice(devices, device);
}

/**
 * Transfer the Main role: demote current Main, promote the target device.
 * Returns the new list. Leaves all other devices as secondary.
 */
export function transferMainRole(
  devices: ConnectedDevice[],
  newMainDeviceId: string,
): ConnectedDevice[] {
  return devices.map((d) => ({
    ...d,
    role: d.deviceId === newMainDeviceId ? "main" : "secondary",
  }));
}

/** Sort: Main first, then by last-seen descending. */
export function sortedConnectedDevices(devices: ConnectedDevice[]): ConnectedDevice[] {
  return [...devices].sort((a, b) => {
    if (a.role === "main" && b.role !== "main") return -1;
    if (b.role === "main" && a.role !== "main") return 1;
    return b.lastSeenUtc.localeCompare(a.lastSeenUtc);
  });
}
